import type { Profile } from '@/types';

// ════════════════════════════════════════════════════════════════
// Реакция Славы на записанный приём еды.
//
// Зачем: Слава жил во вкладке, куда надо было ИДТИ. Здесь он встречает
// человека там, где тот и так находится, — в момент, ради которого
// приложение и открыли. Пять приёмов в день = пять встреч.
//
// ⚠️ БЕЗ МОДЕЛИ, и это принципиально. Реакция на КАЖДЫЙ приём через ИИ —
// это ≈0.3 ₽ за раз; у Нади 22 приёма в день, то есть ~200 ₽/мес на неё одну.
// При сотне активных счёт станет заметным, а Слава превратится в фон,
// который перестают читать. Поэтому: реплика — по правилам и бесплатно,
// модель подключается ТАПОМ, когда человек сам захотел разговора.
//
// Второе правило (хартия ненавязчивости): молчать, когда сказать нечего.
// null здесь — нормальный и частый ответ.
// ════════════════════════════════════════════════════════════════

export type MealFacts = {
  /** КБЖУ этого приёма */
  calories: number;
  protein: number;
  /** Итоги дня ВКЛЮЧАЯ этот приём */
  dayCalories: number;
  dayProtein: number;
  /** Норма */
  goalCalories: number;
  goalProtein: number;
  /** Который это приём за сегодня (1 = первый) */
  mealIndex: number;
  /** Серия дней подряд */
  streak: number;
  hour: number;
};

export type MealReaction = {
  text: string;
  /** Что уйдёт в чат первой репликой, если нажать «спросить» */
  ask: string;
};

export function slavaMealReaction(f: MealFacts, profile?: Profile | null): MealReaction | null {
  const name = profile?.first_name ?? '';
  const proteinPct = f.goalProtein > 0 ? f.dayProtein / f.goalProtein : 1;
  const calPct = f.goalCalories > 0 ? f.dayCalories / f.goalCalories : 0;
  const left = Math.max(0, Math.round(f.goalCalories - f.dayCalories));
  const proteinLeft = Math.max(0, Math.round(f.goalProtein - f.dayProtein));

  // Первый приём человека вообще — момент, когда стоит показать, что он не один.
  if (f.streak <= 1 && f.mealIndex === 1) {
    return {
      text: 'Первая запись есть. Дальше проще — я уже вижу, что ты ешь',
      ask: 'Я только начал записывать еду. С чего мне начать, чтобы был толк?',
    };
  }

  // Ужин без белка — самая частая и самая поправимая история
  if (f.hour >= 17 && proteinPct < 0.6 && proteinLeft > 20) {
    return {
      text: `Белка за день ${Math.round(f.dayProtein)} из ${Math.round(f.goalProtein)} — добери творогом или яйцами`,
      ask: `Сегодня белка ${Math.round(f.dayProtein)} из ${Math.round(f.goalProtein)}. Чем добрать вечером?`,
    };
  }

  // Крупный приём в первой половине дня — не упрёк, а расклад на остаток
  if (calPct >= 0.75 && f.hour < 16) {
    return {
      text: `Уже ${Math.round(calPct * 100)}% нормы, а день не кончился. Осталось ${left} ккал`,
      ask: `Я съел уже ${Math.round(f.dayCalories)} ккал из ${f.goalCalories}, а ещё только день. Что делать с ужином?`,
    };
  }

  // Хороший белковый приём — замечать надо и хорошее, иначе Слава только придирается
  if (f.protein >= 25 && f.goalProtein > 0) {
    return {
      text: `${Math.round(f.protein)}г белка за приём — так и надо`,
      ask: 'Хорошо ли у меня с белком в последние дни?',
    };
  }

  // Норма закрыта — сказать об этом важнее, чем промолчать
  if (proteinPct >= 1 && f.mealIndex >= 2) {
    return {
      text: `Белок на сегодня закрыт${name ? `, ${name}` : ''} — редко у кого так`,
      ask: 'Белок сегодня закрыт. Что дальше — на что смотреть?',
    };
  }

  // Круглая серия — повод, который человек сам не заметит
  if (f.streak >= 7 && f.streak % 7 === 0 && f.mealIndex === 1) {
    return {
      text: `${f.streak} дней подряд без пропусков. Это уже привычка`,
      ask: `У меня ${f.streak} дней подряд записей. Что это говорит о моём питании?`,
    };
  }

  // Во всех остальных случаях — молчим.
  return null;
}
